/**
 * Les demandes d'ouverture du mode `?demo` (ARTO-188) : huit demandes
 * fictives, une par cas utile (nouvelle, qualifiée, convertie, perdue, sans
 * sélection, avec identifiants écartés…), en mémoire. Aucune vraie galerie :
 * des noms inventés.
 *
 * Les règles sont celles de l'API (`apps/api/src/console/demandes-ouverture.ts`) :
 * révision lue, état attendu, `409 demande_convertie`, `409 etat_incompatible`,
 * `409 revision_demande_perimee`. « Créer le dossier » crée un vrai dossier de
 * démonstration, que l'assistant peut ensuite ouvrir.
 */
import {
  CHOIX_RETENUS,
  ETATS_DEMANDE_OUVERTS,
  ETATS_DEMANDE_OUVERTURE,
  type DemandeOuvertureResume,
  type DemandeOuvertureVue,
  type EtatDemandeOuverture,
} from '@artosera/shared';
import {
  ApiError,
  type FiltresDemandesOuverture,
  type ModificationDemandeOuverture,
} from './api.js';
import { compteDemoCourant } from './demoData.js';
import { demoOuvertures } from './demoOuvertures.js';
import { TAILLE_PAGE_DEMANDES } from './demandesOuverture.js';

const JOUR = 86_400_000;
const il_y_a = (jours: number, heures = 0): string =>
  new Date(Date.now() - jours * JOUR - heures * 3_600_000).toISOString();

const AUTRE_RESPONSABLE = { id: '66f0000000000000000000a2', email: 'exploitation@venio.test' };

type Ligne = DemandeOuvertureVue;

function selection(
  partiel: Partial<Ligne['selection']> = {},
  modules: Ligne['selection']['modules'] = [],
): Ligne['selection'] {
  return {
    palier: null,
    modules,
    addons: [],
    services: [],
    accompagnement: [],
    identifiantsIgnores: 0,
    ...partiel,
  };
}

function demande(
  rang: number,
  etat: EtatDemandeOuverture,
  plus: Partial<Ligne> & Pick<Ligne, 'contact' | 'galerie'>,
): Ligne {
  const creeLe = plus.creeLe ?? il_y_a(rang);
  return {
    id: `demo-demande-ouverture-${rang}`,
    numero: `DO-2026-${String(rang).padStart(4, '0')}`,
    etat,
    source: 'composeur',
    revision: 0,
    langue: 'fr',
    selection: selection(),
    message: '',
    consentement: { version: '2026-10', le: creeLe },
    responsable: null,
    dossier: null,
    motifPerte: null,
    notes: [],
    historique: [{ etat: 'nouvelle', par: 'artosera.com', le: creeLe, motif: null }],
    envois: { accuse: creeLe, alerte: creeLe },
    dernierContactLe: creeLe,
    gestes: { qualifier: false, perdre: false, creerDossier: false },
    ...plus,
    creeLe,
  };
}

function recalculerGestes(ligne: Ligne): void {
  const ouverte = ETATS_DEMANDE_OUVERTS.includes(ligne.etat);
  ligne.gestes = {
    qualifier: ligne.etat === 'nouvelle',
    perdre: ouverte,
    creerDossier: ouverte && ligne.dossier === null,
  };
}

let lignes: Ligne[] | null = null;

function semer(): Ligne[] {
  const moi = compteDemoCourant().compte;
  const semees: Ligne[] = [
    demande(1, 'nouvelle', {
      creeLe: il_y_a(0, 2),
      contact: {
        nom: 'Camille Exemple',
        email: 'camille@galerie-exemple.example',
        telephone: '01 02 03 04 05',
        fonction: 'Directrice',
      },
      galerie: {
        nom: 'Galerie Exemple',
        ville: 'Paris',
        pays: 'FR',
        siteWeb: 'https://galerie-exemple.example',
      },
      selection: selection(
        {
          palier: 'galerie',
          addons: [
            { addon: 'pipeline-ventes', choix: 'interessant' },
            { addon: 'boite-reception', choix: 'plus-tard' },
          ],
          services: ['site'],
          accompagnement: [
            { prestation: 'mise-en-place', choix: 'oui' },
            { prestation: 'reprise-donnees', choix: 'a-discuter' },
            { prestation: 'formation', choix: 'oui' },
            { prestation: 'assistance', choix: 'non' },
          ],
        },
        [
          { module: 'facturation', choix: 'indispensable' },
          { module: 'viewing-rooms', choix: 'indispensable' },
          { module: 'mouvements', choix: 'interessant' },
          { module: 'mode-foire', choix: 'interessant' },
          { module: 'newsletter-reseaux', choix: 'plus-tard' },
          { module: 'artsy-artnet', choix: 'pas-pour-nous' },
        ],
      ),
      message:
        'Bonjour,\nNous ouvrons un second espace en janvier et cherchons un outil unique pour les deux lieux.\n\nPouvons-nous parler d’une reprise de notre inventaire (environ 2 400 œuvres) ?\nMerci.',
    }),
    demande(2, 'nouvelle', {
      source: 'contact',
      creeLe: il_y_a(1, 3),
      contact: {
        nom: 'Jules Démo',
        email: 'jules@atelier-demo.example',
        telephone: null,
        fonction: null,
      },
      galerie: { nom: 'Atelier Démo', ville: 'Lyon', pays: 'FR', siteWeb: null },
      message: 'Une question sur les tarifs pour une petite structure.',
    }),
    demande(3, 'qualifiee', {
      creeLe: il_y_a(4),
      langue: 'en',
      contact: {
        nom: 'Morgan Sample',
        email: 'morgan@gallery-sample.example',
        telephone: '+32 2 000 00 00',
        fonction: 'Gallery manager',
      },
      galerie: {
        nom: 'Gallery Sample',
        ville: 'Bruxelles',
        pays: 'BE',
        siteWeb: 'gallery-sample.example',
      },
      selection: selection(
        {
          palier: 'groupe',
          addons: [{ addon: 'tresorerie', choix: 'indispensable' }],
          services: ['site', 'boutique'],
          accompagnement: [{ prestation: 'reprise-complexe', choix: 'oui' }],
          identifiantsIgnores: 2,
        },
        [
          { module: 'facturation', choix: 'indispensable' },
          { module: 'console-groupe', choix: 'indispensable' },
          { module: 'documents-prestige', choix: 'interessant' },
          { module: 'contrats-artistes', choix: 'interessant' },
          { module: 'agenda-presse', choix: 'interessant' },
        ],
      ),
      message: 'We run three spaces in Belgium and want one console for the group.',
      responsable: { id: moi.id, email: moi.email },
      historique: [
        { etat: 'nouvelle', par: 'artosera.com', le: il_y_a(4), motif: null },
        { etat: 'qualifiee', par: moi.email, le: il_y_a(3), motif: null },
      ],
      notes: [
        {
          texte: 'Rappel prévu jeudi, voir leur calendrier de salons.',
          par: moi.email,
          le: il_y_a(3),
        },
      ],
    }),
    demande(4, 'offre_envoyee', {
      creeLe: il_y_a(9),
      contact: {
        nom: 'Inès Maquette',
        email: 'ines@galerie-maquette.example',
        telephone: '06 00 00 00 00',
        fonction: 'Associée',
      },
      galerie: { nom: 'Galerie Maquette', ville: 'Bordeaux', pays: 'FR', siteWeb: null },
      selection: selection(
        { palier: 'essentiel', accompagnement: [{ prestation: 'formation', choix: 'a-discuter' }] },
        [
          { module: 'facturation', choix: 'indispensable' },
          { module: 'coffre', choix: 'interessant' },
        ],
      ),
      responsable: AUTRE_RESPONSABLE,
      historique: [
        { etat: 'nouvelle', par: 'artosera.com', le: il_y_a(9), motif: null },
        { etat: 'qualifiee', par: AUTRE_RESPONSABLE.email, le: il_y_a(8), motif: null },
        { etat: 'offre_envoyee', par: AUTRE_RESPONSABLE.email, le: il_y_a(6), motif: null },
      ],
    }),
    demande(5, 'convertie', {
      creeLe: il_y_a(15),
      contact: {
        nom: 'Théo Essai',
        email: 'theo@espace-essai.example',
        telephone: null,
        fonction: 'Directeur',
      },
      galerie: { nom: 'Espace Essai', ville: 'Nantes', pays: 'FR', siteWeb: null },
      selection: selection({ palier: 'galerie' }, [
        { module: 'facturation', choix: 'indispensable' },
        { module: 'viewing-rooms', choix: 'interessant' },
      ]),
      responsable: { id: moi.id, email: moi.email },
      dossier: { id: '66f000000000000000000001', numero: 'OUV-2026-001' },
      historique: [
        { etat: 'nouvelle', par: 'artosera.com', le: il_y_a(15), motif: null },
        { etat: 'qualifiee', par: moi.email, le: il_y_a(14), motif: null },
        { etat: 'convertie', par: moi.email, le: il_y_a(12), motif: null },
      ],
    }),
    demande(6, 'perdue', {
      source: 'contact',
      creeLe: il_y_a(21),
      contact: {
        nom: 'Sacha Brouillon',
        email: 'sacha@brouillon.example',
        telephone: null,
        fonction: null,
      },
      galerie: { nom: '', ville: null, pays: null, siteWeb: null },
      motifPerte: 'A choisi de rester sur son outil actuel jusqu’à la fin de l’année.',
      historique: [
        { etat: 'nouvelle', par: 'artosera.com', le: il_y_a(21), motif: null },
        {
          etat: 'perdue',
          par: moi.email,
          le: il_y_a(18),
          motif: 'A choisi de rester sur son outil actuel jusqu’à la fin de l’année.',
        },
      ],
      envois: { accuse: il_y_a(21), alerte: null },
    }),
    demande(7, 'qualifiee', {
      creeLe: il_y_a(31),
      contact: {
        nom: 'Lou Prototype',
        email: 'lou@prototype.example',
        telephone: null,
        fonction: 'Chargée de projets',
      },
      galerie: { nom: 'Prototype Galerie', ville: 'Lille', pays: 'FR', siteWeb: null },
      selection: selection({ palier: 'galerie' }, [
        { module: 'facturation', choix: 'interessant' },
        { module: 'mouvements', choix: 'indispensable' },
        { module: 'modeles-contrats', choix: 'interessant' },
      ]),
      historique: [
        { etat: 'nouvelle', par: 'artosera.com', le: il_y_a(31), motif: null },
        { etat: 'qualifiee', par: AUTRE_RESPONSABLE.email, le: il_y_a(30), motif: null },
      ],
    }),
    demande(8, 'nouvelle', {
      creeLe: il_y_a(44),
      contact: {
        nom: 'Noa Test',
        email: 'noa@galerie-test.example',
        telephone: '04 00 00 00 00',
        fonction: 'Fondatrice',
      },
      galerie: { nom: 'Galerie Test', ville: 'Marseille', pays: 'FR', siteWeb: null },
      envois: { accuse: il_y_a(44), alerte: null },
    }),
  ];
  for (const ligne of semees) recalculerGestes(ligne);
  return semees;
}

function toutes(): Ligne[] {
  lignes ??= semer();
  return lignes;
}

function trouver(id: string): Ligne {
  const ligne = toutes().find((candidate) => candidate.id === id);
  if (!ligne) throw new ApiError(404, 'introuvable', 'Demande d’ouverture introuvable.');
  return ligne;
}

function refus(raison: string, message: string, details: Record<string, unknown> = {}): ApiError {
  return new ApiError(409, 'conflit.version', message, { raison, ...details });
}

/** Les contrôles de l'API : demande convertie, état permis, révision lue. */
function exigerGeste(
  ligne: Ligne,
  revision: number,
  permis: readonly EtatDemandeOuverture[],
): void {
  if (ligne.etat === 'convertie') {
    throw refus('demande_convertie', 'Cette demande est convertie.', {
      dossierId: ligne.dossier?.id ?? null,
    });
  }
  if (!permis.includes(ligne.etat)) {
    throw refus('etat_incompatible', 'Ce geste n’est pas possible dans l’état de la demande.', {
      etat: ligne.etat,
    });
  }
  if (ligne.revision !== revision) {
    throw refus('revision_demande_perimee', 'La demande a changé entre-temps.', {
      revision: ligne.revision,
    });
  }
}

function ecrire(ligne: Ligne): Ligne {
  ligne.revision += 1;
  ligne.dernierContactLe = new Date().toISOString();
  recalculerGestes(ligne);
  return structuredClone(ligne);
}

function resume(ligne: Ligne): DemandeOuvertureResume {
  return {
    id: ligne.id,
    numero: ligne.numero,
    etat: ligne.etat,
    source: ligne.source,
    contact: { nom: ligne.contact.nom, email: ligne.contact.email },
    galerie: { nom: ligne.galerie.nom, ville: ligne.galerie.ville, pays: ligne.galerie.pays },
    palier: ligne.selection.palier,
    modulesRetenus: ligne.selection.modules.filter((module) =>
      CHOIX_RETENUS.some((choix) => choix === module.choix),
    ).length,
    responsable: ligne.responsable?.email ?? null,
    dossierId: ligne.dossier?.id ?? null,
    creeLe: ligne.creeLe,
    dernierContactLe: ligne.dernierContactLe,
  };
}

export const demoDemandesOuverture = {
  lister(filtres: FiltresDemandesOuverture) {
    const moi = compteDemoCourant().compte.id;
    const recherche = filtres.q?.trim().toLowerCase() ?? '';
    const retenues = toutes()
      .filter((ligne) => filtres.etat === undefined || ligne.etat === filtres.etat)
      .filter((ligne) => filtres.source === undefined || ligne.source === filtres.source)
      .filter((ligne) => {
        if (filtres.responsable === undefined) return true;
        if (filtres.responsable === 'personne') return ligne.responsable === null;
        const attendu = filtres.responsable === 'moi' ? moi : filtres.responsable;
        return ligne.responsable?.id === attendu;
      })
      .filter(
        (ligne) =>
          recherche === '' ||
          [ligne.numero, ligne.contact.nom, ligne.contact.email, ligne.galerie.nom].some((champ) =>
            champ.toLowerCase().includes(recherche),
          ),
      )
      .sort((a, b) => b.creeLe.localeCompare(a.creeLe));
    const page = filtres.page ?? 1;
    const compteurs = Object.fromEntries(
      ETATS_DEMANDE_OUVERTURE.map((etat) => [
        etat,
        toutes().filter((ligne) => ligne.etat === etat).length,
      ]),
    ) as Record<EtatDemandeOuverture, number>;
    return {
      items: retenues
        .slice((page - 1) * TAILLE_PAGE_DEMANDES, page * TAILLE_PAGE_DEMANDES)
        .map(resume),
      page,
      total: retenues.length,
      compteurs,
    };
  },

  obtenir(id: string): DemandeOuvertureVue {
    return structuredClone(trouver(id));
  },

  modifier(id: string, corps: ModificationDemandeOuverture): DemandeOuvertureVue {
    const ligne = trouver(id);
    exigerGeste(
      ligne,
      corps.revision,
      corps.responsableId === undefined ? ETATS_DEMANDE_OUVERTURE : ETATS_DEMANDE_OUVERTS,
    );
    const moi = compteDemoCourant().compte;
    if (corps.responsableId !== undefined) {
      if (corps.responsableId === null) ligne.responsable = null;
      else if (corps.responsableId === moi.id) ligne.responsable = { id: moi.id, email: moi.email };
      else if (corps.responsableId === AUTRE_RESPONSABLE.id) ligne.responsable = AUTRE_RESPONSABLE;
      else
        throw new ApiError(404, 'introuvable', 'Ce responsable n’est pas un compte de la console.');
    }
    if (corps.note !== undefined) {
      ligne.notes.push({ texte: corps.note, par: moi.email, le: new Date().toISOString() });
    }
    return ecrire(ligne);
  },

  qualifier(id: string, revision: number): DemandeOuvertureVue {
    const ligne = trouver(id);
    exigerGeste(ligne, revision, ['nouvelle']);
    ligne.etat = 'qualifiee';
    ligne.historique.push({
      etat: 'qualifiee',
      par: compteDemoCourant().compte.email,
      le: new Date().toISOString(),
      motif: null,
    });
    return ecrire(ligne);
  },

  perdre(id: string, revision: number, motif: string): DemandeOuvertureVue {
    const ligne = trouver(id);
    exigerGeste(ligne, revision, ETATS_DEMANDE_OUVERTS);
    ligne.etat = 'perdue';
    ligne.motifPerte = motif;
    ligne.historique.push({
      etat: 'perdue',
      par: compteDemoCourant().compte.email,
      le: new Date().toISOString(),
      motif,
    });
    return ecrire(ligne);
  },

  creerDossier(id: string, revision: number) {
    const ligne = trouver(id);
    exigerGeste(ligne, revision, ETATS_DEMANDE_OUVERTS);
    const dossier = demoOuvertures.creer({
      identite: { nomCommercial: ligne.galerie.nom || ligne.contact.nom },
    });
    ligne.etat = 'convertie';
    ligne.dossier = { id: dossier.id, numero: dossier.numero };
    ligne.historique.push({
      etat: 'convertie',
      par: compteDemoCourant().compte.email,
      le: new Date().toISOString(),
      motif: null,
    });
    return { demande: ecrire(ligne), dossier: { id: dossier.id, numero: dossier.numero } };
  },
};
